/**
 * Development Logging Utility
 *
 * Provides wrapper functions for console logging that only execute in development mode.
 * Critical errors are always logged to ensure visibility in production.
 */

const isDev = import.meta.env.DEV;

/**
 * Log only in development mode
 */
export const devLog = (...args: any[]) => {
  if (isDev) {
    console.log(...args);
  }
};

/**
 * Warn only in development mode
 */
export const devWarn = (...args: any[]) => {
  if (isDev) {
    console.warn(...args);
  }
};

/**
 * Error log only in development mode
 */
export const devError = (...args: any[]) => {
  if (isDev) {
    console.error(...args);
  }
};

/**
 * Info log only in development mode
 */
export const devInfo = (...args: any[]) => {
  if (isDev) {
    console.info(...args);
  }
};

/**
 * Start a timer only in development mode
 */
export const devTime = (label: string) => {
  if (isDev) {
    console.time(label);
  }
};

/**
 * End a timer only in development mode
 */
export const devTimeEnd = (label: string) => {
  if (isDev) {
    console.timeEnd(label);
  }
};

/**
 * Log critical errors that should always be visible
 * Use this for:
 * - API/Network failures
 * - Database errors
 * - Authentication failures
 * - Data integrity issues
 */
export const logCriticalError = (context: string, error: any, details?: any) => {
  console.error(`[CRITICAL] ${context}:`, error);
  if (details) {
    console.error('Details:', details);
  }
};

/**
 * Log critical warnings that should always be visible
 */
export const logCriticalWarning = (context: string, message: string, details?: any) => {
  console.warn(`[WARNING] ${context}: ${message}`);
  if (details) {
    console.warn('Details:', details);
  }
};
